import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Package, 
  ShoppingCart, 
  ShoppingBag, 
  Store, 
  Truck, 
  BarChart3, 
  Settings, 
  LogOut,
  Eye,
  Menu,
  X,
  ChevronDown,
  ChevronUp,
  Clock,
  Shield,
  FileText,
  CheckCircle,
  User,
  Percent,
  CreditCard,
  Stethoscope,
  ClipboardList,
  FilePlus,
  BookOpen,
  File,
  Building2,
  Scale,
  ChevronLeft,
  ChevronRight,
  PieChart,
  BookUser,
  ListTodo,
  MessageCircle,
  Table2,
  Trash2,
  StickyNote
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ isOpen, onClose, onCollapse, isMobile }) => {
  const location = useLocation();
  const { user, logout, isAdmin, isManager, isEmployee, getUserStore } = useAuth();
  const userStore = getUserStore();

  const sidebarSections = [
    // Ventes first
    {
      label: 'Ventes',
      icon: ShoppingCart,
      items: [
        ...((isManager() || isAdmin() || isEmployee()) ? [
          { label: 'Nouveau', to: '/ventes/nouveau', icon: FilePlus },
          { label: 'Commandes', to: '/ventes/commandes', icon: ClipboardList },
          { label: 'Cmd supprimées', to: '/ventes/commandes-supprimees', icon: Trash2 },
          { label: 'WhatsApp', to: '/whatsapp', icon: MessageCircle },
          { label: 'Config WhatsApp', to: '/whatsapp/config', icon: Settings },
          { label: 'Feuille Google', to: '/feuille-google', icon: Table2 },
          { label: 'Factures', to: '/ventes/factures', icon: FileText },
        ] : []),
        ...((isManager() || isAdmin()) ? [
          { label: 'Devis', to: '/ventes/devis', icon: FileText },
        ] : []),
        ...((isManager() || isAdmin() || isEmployee()) ? [
          { label: 'Journal des livraisons', to: '/ventes/journal-livraisons', icon: BookOpen },
        ] : []),
        { label: 'Clients', to: '/clients', icon: User },
        { label: 'Promotions', to: '/promotions', icon: Percent },
        { label: 'Crédits', to: '/credits', icon: CreditCard },
        { label: 'Médecins', to: '/medecins', icon: Stethoscope },
      ],
    },
    // Achats second
    {
      label: 'Achats',
      icon: ShoppingBag,
      items: [
        ...(isManager() ? [
          { label: 'Réception commandes', to: '/achats/reception-commandes', icon: Truck },
          { label: 'Avoirs', to: '/achats/avoirs', icon: FileText },
          { label: 'Factures fournisseurs', to: '/achats/factures-fournisseurs', icon: FileText },
        ] : []),
        { label: 'Produits', to: '/products', icon: Package },
        { label: 'Étiquettes Stickers', to: '/stickers', icon: Package },
        { label: 'Fournisseurs', to: '/suppliers', icon: Truck },
      ],
    },
    // Employés & groupes third - Hidden for employees
    ...((isAdmin() || isManager()) ? [{
      label: 'Employés & groupes',
      icon: Users,
      items: [
        ...(isAdmin() ? [{ label: 'Employés', to: '/employes', icon: Users }] : []),
        { label: 'Pointage', to: '/pointage', icon: Clock },
      ],
    }] : []),
    // Add Paiements section
    {
      label: 'Paiements',
      icon: CreditCard,
      items: [
        { label: 'Liste des paiements', to: '/paiements', icon: CreditCard },
        { label: 'Dépenses', to: '/depenses', icon: FileText },
        { label: 'Gestion des chèques', to: '/gestion-cheques', icon: CreditCard },
      ],
    },
    // RAPPORT (Convention + Contacts sont des liens séparés juste en dessous dans le JSX)
    ...((isAdmin() || isManager()) ? [{
      label: 'RAPPORT',
      icon: PieChart,
      items: [
        { label: 'Génération de rapports', to: '/rapport', icon: FileText },
      ],
    }] : []),
    // Admin only sections (Administration, Points de vente)
    ...(isAdmin() ? [{
      label: 'Administration',
      icon: Shield,
      items: [
        { label: 'Gestion des magasins', to: '/admin/stores', icon: Building2 },
        { label: 'Gestion des employés', to: '/employes', icon: Users },
        { label: 'Privilèges', to: '/privileges', icon: Shield },
        { label: 'Gestion des privilèges de pages', to: '/page-privileges', icon: Settings },
      ],
    }] : []),
    ...(isAdmin() ? [{
      label: 'Points de vente',
      icon: Store,
      items: [
        { label: 'Liste des magasins', to: '/stores', icon: Store },
        { label: 'Factures', to: '/points-de-vente/factures', icon: FileText },
      ],
    }] : []),
    ...(isAdmin() ? [{
      label: 'Gestion des Paiements',
      icon: CreditCard,
      items: [
        { label: 'Vue d\'ensemble', to: '/paiements/vue-d-ensemble', icon: BarChart3 },
        { label: 'Historique', to: '/paiements/historique', icon: FileText },
      ],
    }] : []),
  ];

  const [openSections, setOpenSections] = React.useState({});
  const [collapsed, setCollapsed] = React.useState(false);
  
  const handleCollapseToggle = () => {
    if (!isMobile) {
      setCollapsed((c) => !c);
    }
  };

  React.useEffect(() => {
    if (onCollapse) onCollapse(collapsed);
  }, [collapsed, onCollapse]);

  const toggleSection = (label) => {
    setOpenSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleLogout = () => {
    logout();
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'badge-danger';
      case 'manager':
        return 'badge-primary';
      case 'employee':
        return 'badge-success';
      default:
        return 'badge-neutral';
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && (
        <div 
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
            isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 left-0 z-50 h-screen bg-white shadow-2xl transition-all duration-300 ease-out flex flex-col ${
        isMobile 
          ? `${isOpen ? 'w-80' : 'w-0'} overflow-hidden` 
          : collapsed 
            ? 'w-20' 
            : 'w-64'
      }`}>
        {/* Collapse/Expand Button - Desktop Only */}
        {!isMobile && (
          <button
            className="absolute top-4 -right-3 z-50 bg-white border rounded-full shadow p-1 hover:bg-neutral-100 transition-all"
            onClick={handleCollapseToggle}
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-neutral-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center shadow-lg">
              <Eye className="w-6 h-6 text-white" />
            </div>
            {(!collapsed || isMobile) && (
              <div>
                <h1 className="text-lg font-bold text-gradient">EPOPTIQUE</h1>
                <p className="text-xs text-neutral-500">Management System</p>
              </div>
            )}
          </div>
          {isMobile && (
            <button
              onClick={onClose}
              className="btn btn-ghost btn-sm p-1"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        
        {/* Store Info */}
        {userStore.name && (!collapsed || isMobile) && (
          <div className="px-4 sm:px-6 py-4 bg-gradient-neutral border-b border-neutral-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                <Store className="w-4 h-4 text-primary-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-neutral-900">{userStore.name}</p>
                {userStore.city && (
                  <p className="text-xs text-neutral-500">{userStore.city}</p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Navigation Container */}
        <div className="sidebar-scroll">
          {/* Tableau de bord direct link */}
          <nav className="px-4 pt-6 pb-2 space-y-1">
            <Link
              to="/"
              onClick={onClose}
              className={`nav-item ${location.pathname === '/' ? 'nav-item-active' : ''} flex items-center`}
            >
              <Home className="w-5 h-5 mr-3" />
              {(!collapsed || isMobile) && <span className="text-base font-semibold">Tableau de bord</span>}
            </Link>
          </nav>
          
          {/* Navigation with dropdowns for sections */}
          <nav className="px-4 py-2 space-y-2">
            {sidebarSections.map((section) => (
              <React.Fragment key={section.label}>
                <div>
                  <button
                    type="button"
                    className={`flex items-center w-full px-3 py-3 text-left font-semibold text-neutral-500 hover:text-neutral-700 focus:outline-none uppercase text-xs tracking-wider rounded-lg hover:bg-neutral-100 transition-all duration-200 ${
                      collapsed && !isMobile ? 'justify-center' : ''
                    }`}
                    onClick={() => toggleSection(section.label)}
                  >
                    <section.icon className="w-5 h-5 mr-3" />
                    {(!collapsed || isMobile) && section.label}
                    {(!collapsed || isMobile) && (openSections[section.label] ? (
                      <ChevronUp className="ml-auto w-4 h-4 transition-transform duration-200" />
                    ) : (
                      <ChevronDown className="ml-auto w-4 h-4 transition-transform duration-200" />
                    ))}
                  </button>
                  {(!collapsed || isMobile) && openSections[section.label] && (
                    <div className="ml-6 mt-2 space-y-1 animate-fade-in-up">
                      {section.items.map((item) => (
                        <Link
                          key={item.label}
                          to={item.to}
                          onClick={onClose}
                          className={`nav-item ${location.pathname === item.to ? 'nav-item-active' : ''}`}
                        >
                          <item.icon className="w-4 h-4 mr-3" />
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                {/* Liens généraux sous RAPPORT (pas dans le menu déroulant) */}
                {section.label === 'RAPPORT' && (isAdmin() || isManager()) && (!collapsed || isMobile) && (
                  <div className="pl-1 pr-1 pb-2 space-y-1 border-b border-neutral-100 mb-1">
                    <p className="px-3 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                      Général
                    </p>
                    <Link
                      to="/convention"
                      onClick={onClose}
                      className={`nav-item flex items-center ${location.pathname === '/convention' ? 'nav-item-active' : ''}`}
                    >
                      <Scale className="w-4 h-4 mr-3 shrink-0" />
                      Convention
                    </Link>
                    <Link
                      to="/contacts"
                      onClick={onClose}
                      className={`nav-item flex items-center ${location.pathname === '/contacts' ? 'nav-item-active' : ''}`}
                    >
                      <BookUser className="w-4 h-4 mr-3 shrink-0" />
                      Contacts
                    </Link>
                    <Link
                      to="/pointage"
                      onClick={onClose}
                      className={`nav-item flex items-center ${location.pathname === '/pointage' ? 'nav-item-active' : ''}`}
                    >
                      <Clock className="w-4 h-4 mr-3 shrink-0" />
                      Pointage journalier
                    </Link>
                    <p className="px-3 pt-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                      Tâches
                    </p>
                    <Link
                      to="/todos"
                      onClick={onClose}
                      className={`nav-item flex items-center ${location.pathname === '/todos' ? 'nav-item-active' : ''}`}
                    >
                      <ListTodo className="w-4 h-4 mr-3 shrink-0" />
                      Tâches & chronos
                    </Link>
                    <Link
                      to="/notes"
                      onClick={onClose}
                      className={`nav-item flex items-center ${location.pathname === '/notes' ? 'nav-item-active' : ''}`}
                    >
                      <StickyNote className="w-4 h-4 mr-3 shrink-0" />
                      Notes
                    </Link>
                  </div>
                )}
                {/* Employés : pas de bloc RAPPORT — mêmes liens sous Paiements */}
                {section.label === 'Paiements' && isEmployee() && (!collapsed || isMobile) && (
                  <div className="pl-1 pr-1 pb-2 space-y-1 border-b border-neutral-100 mb-1">
                    <p className="px-3 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                      Général
                    </p>
                    <Link
                      to="/pointage"
                      onClick={onClose}
                      className={`nav-item flex items-center ${location.pathname === '/pointage' ? 'nav-item-active' : ''}`}
                    >
                      <Clock className="w-4 h-4 mr-3 shrink-0" />
                      Pointage
                      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-semibold">
                        Obligatoire
                      </span>
                    </Link>
                    <Link
                      to="/convention"
                      onClick={onClose}
                      className={`nav-item flex items-center ${location.pathname === '/convention' ? 'nav-item-active' : ''}`}
                    >
                      <Scale className="w-4 h-4 mr-3 shrink-0" />
                      Convention
                    </Link>
                    <Link
                      to="/contacts"
                      onClick={onClose}
                      className={`nav-item flex items-center ${location.pathname === '/contacts' ? 'nav-item-active' : ''}`}
                    >
                      <BookUser className="w-4 h-4 mr-3 shrink-0" />
                      Contacts
                    </Link>
                    <p className="px-3 pt-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                      Tâches
                    </p>
                    <Link
                      to="/todos"
                      onClick={onClose}
                      className={`nav-item flex items-center ${location.pathname === '/todos' ? 'nav-item-active' : ''}`}
                    >
                      <ListTodo className="w-4 h-4 mr-3 shrink-0" />
                      Tâches & chronos
                    </Link>
                    <Link
                      to="/notes"
                      onClick={onClose}
                      className={`nav-item flex items-center ${location.pathname === '/notes' ? 'nav-item-active' : ''}`}
                    >
                      <StickyNote className="w-4 h-4 mr-3 shrink-0" />
                      Notes
                    </Link>
                  </div>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
        
        {/* User Info fixed at bottom */}
        {(!collapsed || isMobile) && (
          <div className="flex-shrink-0 p-4 border-t border-neutral-200 bg-neutral-50">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center shadow-lg">
                <span className="text-sm font-bold text-white">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-neutral-900 truncate">
                  {user?.name}
                </p>
                <div className="flex items-center space-x-2">
                  <span className={`badge ${getRoleBadgeColor(user?.role)}`}>
                    {user?.role}
                  </span>
                  {userStore.name && (
                    <span className="text-xs text-neutral-400">•</span>
                  )}
                  {userStore.name && (
                    <p className="text-xs text-neutral-500 truncate">
                      {userStore.name}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full btn btn-outline btn-sm"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
            {isEmployee() && (
              <Link
                to="/pointage"
                onClick={onClose}
                className="mt-2 w-full inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-rose-600 shadow-md shadow-rose-400/30 hover:brightness-105 transition-all"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Check-out
              </Link>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar; 